import { useEffect, useRef, useState } from "react";

import type { I_Location } from "../../shared/types.ts";
import { searchLocations } from "../api.ts";

interface I_OriginPicker {
    origin: I_Location | null
    countries: string[]
    includeOrgs: boolean
    onSelect: (location: I_Location) => void
}

export const describe = (location: I_Location): string => {
    return [location.name, location.name2].filter(Boolean).join(" ");
}

// Bei kyrillisch oder koreanisch geschriebenen Orten wäre sonst nicht zu sehen, weshalb der
// Treffer auf "Moscow" die Zeile "Москва" ist
const latinHint = (location: I_Location): string => {
    if (!location.nameLatin || location.nameLatin === location.name) return "";

    return ` (${location.nameLatin})`;
}

const OriginPicker = ({ origin, countries, includeOrgs, onSelect }: I_OriginPicker) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<I_Location[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState("");
    const request = useRef(0);

    useEffect(() => {
        if (query.trim().length < 2) {
            setResults([]);

            return;
        }

        const id = ++request.current;
        const timer = setTimeout(() => {
            searchLocations(query, countries, includeOrgs)
                .then((found) => {
                    if (id !== request.current) return; // A newer keystroke already took over

                    setResults(found.results);
                    setHasMore(found.hasMore);
                    setError("");
                })
                .catch((problem) => setError(problem.message));
        }, 200);

        return () => clearTimeout(timer);
    }, [query, countries, includeOrgs]);

    const select = (location: I_Location) => {
        onSelect(location);
        setQuery("");
        setResults([]);
        setOpen(false);
    };

    return (
        <div className="picker">
            <label htmlFor="origin">Startort</label>

            <input
                id="origin"
                type="text"
                autoComplete="off"
                placeholder="Ort oder Postleitzahl eingeben"
                value={query}
                onChange={(event) => {
                    setQuery(event.target.value);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
            />

            {origin && !query && (
                <p className="picker-selected">
                    <strong>{describe(origin)}{latinHint(origin)}</strong>
                    <span>
                        {origin.zipCount > 1 ? `${origin.zipCount} PLZ` : origin.zipcode} · {origin.countryCode}{origin.admin1 ? ` · ${origin.admin1}` : ""}
                    </span>
                </p>
            )}

            {error && <p className="error">{error}</p>}

            {open && results.length > 0 && (
                <ul className="picker-results">
                    {results.map((location) => (
                        <li key={location.id}>
                            <button onMouseDown={() => select(location)}>
                                <span className="picker-name">{describe(location)}{latinHint(location)}</span>
                                <span className="picker-meta">
                                    {location.zipCount > 1 ? `${location.zipCount} PLZ` : location.zipcode} · {location.countryCode}{location.admin2 ? ` · ${location.admin2}` : ""}
                                </span>
                            </button>
                        </li>
                    ))}

                    {hasMore && <li className="picker-more">Weitere Treffer vorhanden, Suche verfeinern</li>}
                </ul>
            )}
        </div>
    )
}
export default OriginPicker;